import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../pages/app/protected/firebase';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function PublicReceipt() {
  const { id } = useParams();
  const ref = useRef();
  const [receipt, setReceipt] = useState(null);
  const [companySnap, setCompanySnap] = useState({});
  const [companyInfo, setCompanyInfo] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadReceipt = async () => {
      try {
        // Get public ID from URL query parameter
        const searchParams = new URLSearchParams(window.location.search);
        const publicId = searchParams.get('pid');
        
        if (!publicId) {
          console.error('No public ID provided in URL');
          setLoading(false);
          return;
        }

        // Get the receipt using both public ID and receipt ID
        const receiptDoc = await getDoc(doc(db, 'publicUsers', publicId, 'receipts', id));
        
        if (!receiptDoc.exists()) {
          console.error('Receipt not found');
          setLoading(false);
          return;
        }
        
        const data = receiptDoc.data();
        setReceipt(data);
        
        setCompanySnap({
          companyName: data.companyName,
          logoUrl: data.companyLogo,
          street: data.companyStreet,
          city: data.companyCity,
          postcode: data.companyPostcode,
          state: data.companyState,
          country: data.companyCountry,
          countryCode: data.companyCountryCode,
          phone: data.companyPhone,
          website: data.companyWebsite,
          sstNumber: data.companySSTNumber,
          regType: data.companyRegType,
          regNumber: data.companyRegNumber,
        });
        
        setCompanyInfo({
          thankYouNote: data.thankYouNote,
          receiptFooter: data.footer,
        });
      } catch (err) {
        console.error('Error loading receipt', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadReceipt();
  }, [id]);
  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}`;
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const downloadPDF = async () => {
    if (receipt?.paymentStatus === 'Void') return; // prevent download
    const element = ref.current;
    const canvas = await html2canvas(element);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [canvas.width, canvas.height] });
    pdf.addImage(imgData, 'PNG', 0, 0);
    pdf.save(`receipt_${id}.pdf`);
  };

  if (loading) return <div className="p-4 text-center">Loading...</div>;
  if (!receipt) return <div className="p-4 text-center text-red-500">Receipt not found.</div>;

  const taxRate = receipt.taxAmount && receipt.subtotal ? ((receipt.taxAmount / receipt.subtotal) * 100).toFixed(1) : 0;
  const rounding = receipt.rounding || 0;
  const formatPrice = (num) => `RM${Number(num || 0).toFixed(2)}`;
  const formatDelta = (num) => `(+${formatPrice(num)})`;
  const formatVariant = (item) => {
    if (!item) return '';
    if (item.variantName) return item.variantName;
    if (item.variantLabel) return item.variantLabel;
    if (item.variant && (item.variant.name || item.variant.label)) return item.variant.name || item.variant.label;
    const opts = item.selectedOptions || item.options;
    if (Array.isArray(opts) && opts.length > 0) {
      return opts.map(o => `${o.optionLabel || o.optKey || o.group}: ${o.valueLabel || o.valueKey || o.value}`).join(' / ');
    }
    return '';
  };
  const getBaseAndDelta = (item) => {
    const optionDelta = Array.isArray(item.selectedOptions)
      ? item.selectedOptions.reduce((sum, o) => sum + Number(o.price || 0), 0)
      : 0;
    const delta = Number(item.variantPriceDelta || 0) || optionDelta;
    const base = Number(item.price || 0) - delta;
    return { base, delta };
  };

  return (
    <div className="flex flex-col items-center p-4">
      <div className="relative">
        {receipt.paymentStatus === 'Void' && (
          <div className="absolute inset-0 flex justify-center items-center z-20 pointer-events-none">
            <h1 className="text-[100px] font-extrabold text-red-600 opacity-50 rotate-45">VOID</h1>
          </div>
        )}
        <div ref={ref} className="bg-white text-black w-[300px] p-6 rounded-lg shadow-lg text-sm relative z-10">
          {companySnap.logoUrl && (
            <img src={companySnap.logoUrl} alt="Logo" className="h-16 mx-auto mb-4 object-contain" />
          )}
          <h3 className="text-center text-xl font-bold mb-1">{companySnap.companyName}</h3>
          {companySnap.regNumber && (companySnap.regType || '').toUpperCase() === 'BRN' && (
            <p className="text-center text-xs mb-1">
              {'Reg. Num: ' + companySnap.regNumber}
            </p>
          )}
          {companySnap.sstNumber && (
            <p className="text-center text-xs mb-1">SST Num: {companySnap.sstNumber}</p>
          )}
          <p className="text-center text-xs mb-2">
            {[companySnap.street, companySnap.postcode, companySnap.city, companySnap.state, companySnap.country].filter(Boolean).join(', ')}
          </p>
          <hr className="my-2 border-dashed border-gray-400" />
          {receipt.queueNumber && (
            <p className="text-l font-bold">Queue No: {String(receipt.queueNumber).padStart(3, '0')}</p>
          )}
          {receipt.deliveryNum && (
            <p className="text-l font-bold">Delivery No: {String(receipt.deliveryNum).padStart(3, '0')}</p>
          )}
          <p className="text-xs">
            Date: {formatDate(receipt.timestamp)} {formatTime(receipt.timestamp)}<br />
            Receipt No: {receipt.receiptNumber}<br />
            Mode: {receipt.mode}
          </p>
          <hr className="my-2 border-dashed border-gray-400" />
          <div className="flex justify-between font-bold"><span>Item</span><span>Price</span></div>
          {receipt.items.map((item, idx) => (
            <div key={idx} className="mb-1">
              <div className="flex justify-between text-xs">
                <div>
                  <span className="font-medium">{item.name} x{item.qty}</span>
                  {formatVariant(item) && (
                    <div className="text-[11px] text-gray-500">
                      {formatVariant(item)}
                      {(() => {
                        const optionDelta = Array.isArray(item.selectedOptions)
                          ? item.selectedOptions.reduce((s, o) => s + Number(o.price || 0), 0)
                          : 0;
                        const deltaTotal =
                          (Number(item.variantPriceDelta || 0) || optionDelta) *
                          (item.qty || 1);
                        return deltaTotal !== 0 ? ` ${formatDelta(deltaTotal)}` : '';
                      })()}
                    </div>
                  )}
                  {item.items && item.items.length > 0 && (
                    <div className="ml-3 mt-1 text-gray-600">
                      {item.items.map((bundleItem, bIdx) => {
                        const subQty = (bundleItem.quantity || 1) * (item.qty || 1);
                        const subOptDelta = Array.isArray(bundleItem.selectedOptions)
                          ? bundleItem.selectedOptions.reduce((s, o) => s + Number(o.price || 0), 0)
                          : 0;
                        const subDelta = (Number(bundleItem.variantPriceDelta || 0) || subOptDelta) * subQty;
                        return (
                          <div key={bIdx} className="text-xs">
                            + {bundleItem.name} x{subQty}
                            {formatVariant(bundleItem) && (
                              <div className="ml-4 text-[10px] text-gray-500">
                                {formatVariant(bundleItem)}
                                {subDelta !== 0 ? ` (+${formatPrice(subDelta)})` : ''}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {(() => {
                  const { base, delta } = getBaseAndDelta(item);
                  const baseTotal = base * item.qty;
                  const deltaTotal = delta * item.qty;
                  return (
                    <div className="text-right">
                      <div>{formatPrice(baseTotal)}</div>
                      {deltaTotal !== 0 && (
                        <div className="text-[11px] text-gray-500">+{formatPrice(deltaTotal)}</div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
          <hr className="my-2 border-dashed border-gray-400" />
          <div className="text-xs space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>RM{receipt.subtotal.toFixed(2)}</span></div>
            {receipt.sst6Amount > 0 && (
              <div className="flex justify-between"><span>SST (6%)</span><span>RM{receipt.sst6Amount.toFixed(2)}</span></div>
            )}
            {receipt.sst8Amount > 0 && (
              <div className="flex justify-between"><span>SST (8%)</span><span>RM{receipt.sst8Amount.toFixed(2)}</span></div>
            )}
            {receipt.serviceTax10Amount > 0 && (
              <div className="flex justify-between"><span>Service Tax {receipt.serviceTax10Rate ? `(${receipt.serviceTax10Rate}%)` : ''}</span><span>RM{receipt.serviceTax10Amount.toFixed(2)}</span></div>
            )}
            {receipt.discount && receipt.discount !== '-' && (
              <div className="flex justify-between"><span>Discount</span><span>-RM{receipt.discountAmount.toFixed(2)}</span></div>
            )}
            {receipt.rounding !== "-0.00" && receipt.rounding !== "0.00" && (
              <div className="flex justify-between"><span>Rounding</span><span>{(rounding >= 0 ? '+' : '') + Number(rounding).toFixed(2)}</span></div>
            )}
            <div className="flex justify-between font-bold"><span>Total</span><span>RM{receipt.total.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>{receipt.paymentMethod}</span><span>RM{receipt.received.toFixed(2)}</span></div>
            {receipt.paymentMethod === "Cash" && (
              <div className="flex justify-between"><span>Change</span><span>RM{receipt.balance.toFixed(2)}</span></div>
            )}
          </div>
          <p className="text-center text-xs mb-2">{companyInfo.thankYouNote || 'Thank you and come again!'}</p>
          {companyInfo?.receiptFooter && <div className="text-center text-xs text-gray-600 mt-2">{companyInfo.receiptFooter}</div>}<br />
          <p className="text-center text-xs mb-2">Powered by CashMate</p>
        </div>
      </div>

      {receipt.paymentStatus !== 'Void' && (
        <button onClick={downloadPDF} className="mt-4 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700">
          Download Receipt
        </button>
      )}
      {receipt.paymentStatus === 'Void' && (
        <p className="mt-4 text-center text-xs text-gray-600">Reason for void: {receipt.voidReason}</p>
      )}
    </div>
  );
}
